// Whether a receipt's Undo still applies (TOM-381). A receipt is drawn again
// from the chat's history after a reload (TOM-376), and again when the Neo panel
// reopens; neither remembers an Undo it already did. So each part that would
// offer Undo asks its own route first: a board's read, a name change's read,
// or the boat's equipment list (TOM-408). The step applies while its version
// is the one the receipt holds; an undo, an owner's change or a later request
// clears or replaces it (cloud ADR 0302 §3, ADR 0312 §9).

import { api } from '../api';
import { withBoardKinds } from './neoBoard';
import type { EquipmentReceiptPart, ReceiptPart } from './neoReceipt';

/**
 * `applies`: Undo still undoes this part. `gone`: it doesn't (undone, or changed
 * since). `missing`: the board isn't there any more, so there is nothing to
 * open either. `unknown`: the route couldn't be read, so the receipt keeps its
 * Undo, whose own answer is honest either way.
 */
export type ReceiptStep = 'applies' | 'gone' | 'missing' | 'unknown';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function statusOf(err: unknown): number | undefined {
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/** The undo step a route's answer carries: the board read's, or the name change's. */
export function undoStepOf(part: ReceiptPart, body: unknown): { version: number } | null {
  const holder = part.kind === 'board' ? (isRecord(body) ? body.board : null) : body;
  const undo = isRecord(holder) ? holder.undo : null;
  return isRecord(undo) && typeof undo.version === 'number' ? { version: undo.version } : null;
}

/**
 * An equipment part's step applies while some answer the request wrote is
 * still Neo's at the version the request left it. The list shows no step, but
 * an undo, an owner's change and a later request each move the version.
 *
 * Gear that sends no readings leaves the list once its make and model are
 * cleared (cloud ADR 0425), so a make and model the request forgot is not there
 * to compare: while that gear is still missing, the step is taken to apply and
 * POST …/equipment/undo decides (TOM-878). A make and model the request set
 * and an undo removed is missing too, and is gone.
 */
export function equipmentStepOf(part: EquipmentReceiptPart, body: unknown): 'applies' | 'gone' {
  const list = isRecord(body) && Array.isArray(body.instances) ? body.instances : [];
  const gear = isRecord(body) && Array.isArray(body.gear) ? body.gear : [];
  const versions = new Map<string, number>();
  // The gear whose make and model the list can show: an entry that carries the answer.
  const listedGear = new Set<string>();
  const neoVersion = (kind: string, instance: string, state: unknown) => {
    if (kind === 'make_model') listedGear.add(instance);
    if (isRecord(state) && state.origin === 'neo' && typeof state.version === 'number') {
      versions.set(`${kind} ${instance}`, state.version);
    }
  };
  for (const entry of list) {
    if (!isRecord(entry) || typeof entry.instance !== 'string') continue;
    const answers = isRecord(entry.answers) ? entry.answers : {};
    neoVersion('presence', entry.instance, answers.presence);
    // The make and model of the boat's own gear (cloud ADR 0425, TOM-878).
    if ('makeModel' in answers) neoVersion('make_model', entry.instance, answers.makeModel);
  }
  for (const entry of gear) {
    if (!isRecord(entry) || typeof entry.instance !== 'string') continue;
    neoVersion('make_model', entry.instance, entry.makeModel);
  }
  return part.answers.some(
    (a) =>
      a.version !== null &&
      (versions.get(`${a.kind ?? 'presence'} ${a.subject}`) === a.version ||
        (a.kind === 'make_model' && a.cleared === true && !listedGear.has(a.subject)))
  )
    ? 'applies'
    : 'gone';
}

export async function receiptStepApplies(part: ReceiptPart): Promise<ReceiptStep> {
  if (part.undoVersion === null) return 'gone';
  // A note's step comes with the conversation read back (its records'
  // `undoable`, TOM-611): gone when none of the request's notes holds it. On
  // the live stream the receipt keeps its Undo, whose answer says what it
  // could undo.
  if (part.kind === 'notebook') return part.stepGone ? 'gone' : 'unknown';
  if (part.kind === 'equipment') {
    try {
      return equipmentStepOf(part, await api<unknown>(`/v1/boats/${part.boatId}/equipment`));
    } catch (err) {
      return statusOf(err) === 404 ? 'gone' : 'unknown';
    }
  }
  const path =
    part.kind === 'label'
      ? `/v1/neo/label-changes/${part.requestId}`
      : // With the kinds the web draws, or a system board Neo changed reads as missing (TOM-358).
        withBoardKinds(`/v1/boats/${part.boatId}/boards/${encodeURIComponent(part.slug)}`);
  try {
    const step = undoStepOf(part, await api<unknown>(path));
    return step !== null && step.version === part.undoVersion ? 'applies' : 'gone';
  } catch (err) {
    // A board Neo made and an undo removed is not there to read.
    if (statusOf(err) === 404) return part.kind === 'board' ? 'missing' : 'gone';
    return 'unknown';
  }
}
